"use client";

import type { StaticImageData } from "next/image";

import Image from "next/image";
import { AnimatePresence, m } from "motion/react";
import { PlayPauseButton } from "../PlayPauseButton";

export interface DiscImageConfig {
  src: StaticImageData | string;
  alt: string;
  /** Horizontal offset as percentage (0-100), 50 = centered */
  offsetX?: number;
  /** Vertical offset as percentage (0-100), 50 = centered */
  offsetY?: number;
  /** Blur amount in pixels */
  blur?: number;
  /** Responsive sizes attribute for next/image optimization */
  sizes?: string;
}

export interface DiscColors {
  primary: string;
  secondary: string;
  accent?: string;
  highlight?: string;
}

export function DiscImage({ image }: { image: DiscImageConfig }) {
  const offsetX = image.offsetX ?? 50;
  const offsetY = image.offsetY ?? 50;

  return (
    <Image
      src={image.src}
      alt={image.alt}
      fill
      sizes={image.sizes}
      className='tw-pointer-events-none tw-object-cover'
      style={{
        objectPosition: `${offsetX}% ${offsetY}%`,
        objectFit: "cover",
        filter: image.blur ? `blur(${image.blur}px)` : undefined,
      }}
    />
  );
}

export function DiscGradient({ colors }: { colors: DiscColors }) {
  const { primary, secondary, accent, highlight } = colors;

  return (
    <>
      {/* Base background */}
      <div
        className='tw-absolute tw-inset-[-20%] tw-h-[140%] tw-w-[140%]'
        style={{
          background: `linear-gradient(180deg, ${primary} 0%, ${primary}dd 50%, ${primary}aa 100%)`,
        }}
      />
      {/* Large blob - left side */}
      <div
        className='tw-absolute'
        style={{
          width: "160%",
          height: "160%",
          left: "-80%",
          top: "-30%",
          background: `radial-gradient(ellipse 70% 80% at 70% 50%, ${secondary} 0%, ${secondary}ee 25%, ${secondary}80 45%, transparent 65%)`,
          filter: "blur(15px)",
        }}
      />
      {/* Secondary blob - bottom left */}
      <div
        className='tw-absolute'
        style={{
          width: "120%",
          height: "120%",
          left: "-40%",
          bottom: "-60%",
          background: `radial-gradient(ellipse 60% 70% at 60% 30%, ${secondary}ee 0%, ${secondary}99 30%, transparent 55%)`,
          filter: "blur(12px)",
        }}
      />
      {/* Accent blob - top area */}
      <div
        className='tw-absolute'
        style={{
          width: "140%",
          height: "100%",
          left: "-20%",
          top: "-50%",
          background: `radial-gradient(ellipse 50% 60% at 50% 70%, ${accent || primary}cc 0%, ${accent || primary}66 35%, transparent 55%)`,
          filter: "blur(18px)",
        }}
      />
      {/* Highlight blob - bottom right */}
      <div
        className='tw-absolute'
        style={{
          width: "100%",
          height: "100%",
          right: "-30%",
          bottom: "-40%",
          background: `radial-gradient(ellipse 60% 60% at 40% 40%, ${highlight || secondary}dd 0%, ${highlight || secondary}77 35%, transparent 55%)`,
          filter: "blur(14px)",
        }}
      />
      {/* Center glow */}
      <div
        className='tw-absolute tw-inset-[10%]'
        style={{
          background: `radial-gradient(circle at 50% 50%, rgba(255,255,255,0.15) 0%, transparent 50%)`,
          filter: "blur(8px)",
        }}
      />
    </>
  );
}

export function DiscSolidBackground() {
  return <div className='tw-absolute tw-inset-0 tw-bg-[#EBE9E7]' />;
}

function getImageKey(image: DiscImageConfig | undefined): string {
  if (!image) return "gradient";
  return typeof image.src === "string" ? image.src : image.src.src;
}

function DiscBackgroundContent({
  image,
  colors,
  showGradient,
}: {
  image?: DiscImageConfig;
  colors: DiscColors;
  showGradient: boolean;
}) {
  if (image) return <DiscImage image={image} />;
  if (showGradient) return <DiscGradient colors={colors} />;
  return <DiscSolidBackground />;
}

export function DiscBackground({
  image,
  colors,
  showGradient,
  animate,
}: {
  image?: DiscImageConfig;
  colors: DiscColors;
  showGradient: boolean;
  animate: boolean;
}) {
  const content = <DiscBackgroundContent image={image} colors={colors} showGradient={showGradient} />;

  if (!animate) return content;

  return (
    <AnimatePresence initial={false} mode='popLayout'>
      <m.div
        key={getImageKey(image)}
        className='tw-absolute tw-inset-0'
        initial={{ opacity: 0, scale: 1.05 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 1.05 }}
        transition={{ duration: 0.4, ease: "easeInOut" }}
      >
        {content}
      </m.div>
    </AnimatePresence>
  );
}

export function PlayPauseDiscButton({
  isPlaying,
  onPlayPauseClick,
}: {
  isPlaying: boolean;
  onPlayPauseClick?: () => void;
}) {
  return (
    <PlayPauseButton
      isPlaying={isPlaying}
      onClick={onPlayPauseClick}
      iconSize='sm'
      className='tw-h-9 tw-w-9 tw-rounded-3xl sm:tw-h-11 sm:tw-w-11 tw-bg-[#F5F3F1] tw-backdrop-blur-[10px] tw-text-black tw-transition-all'
    />
  );
}
